import {
  IPriceListPanelProps,
  IListPrices,
  ICommonListProps,
} from '../../areas/product/components/price-list/price-list-panel/types';
import { OptionTypeEnum } from 'src/components/filter-dropdown';
import { IFilter } from 'src/components/price-list/types';
import { AlternativePrice, StockStatus } from 'src/types';
import { replaceMainOfferWithAlternativeOffer } from './utils';
import { ExtendedPrice } from 'src/areas/product/components/product-context';

const placeAfter = 1;
const placeBefore = -1;

type SortablePrice = ExtendedPrice | AlternativePrice;

export const getCommonListProps = (
  props: IPriceListPanelProps
): ICommonListProps => {
  return {
    productName: props.productName,
    media: props.media,
    brand: props.brand,
    campaign: props.campaign,
  };
};

const sortDownloadable = (a: SortablePrice, b: SortablePrice): number => {
  if (
    a.availability?.condition === 'Download' &&
    b.availability?.condition === 'Download'
  )
    return 0;
  if (
    a.availability?.condition === 'Download' &&
    b.availability?.condition !== 'Download'
  )
    return placeBefore;
  if (
    a.availability?.condition !== 'Download' &&
    b.availability?.condition === 'Download'
  )
    return placeAfter;
  return 0;
};

const sortFeatured = (a: SortablePrice, b: SortablePrice): number => {
  if (!('store' in a) || !('store' in b)) return 0;
  if (a.store?.featured && b.store?.featured) return 0;
  if (a.store?.featured && !b.store?.featured) return placeBefore;
  if (!a.store?.featured && b.store?.featured) return placeAfter;
  return 0;
};

const sortStockStatus = (a: SortablePrice, b: SortablePrice): number => {
  const aStockStatus = getNumericSortStock(a.stock?.status ?? undefined);
  const bStockStatus = getNumericSortStock(b.stock?.status ?? undefined);

  return aStockStatus - bStockStatus;
};

const getNumericSortStock = (stockStatus?: StockStatus): number => {
  switch (stockStatus) {
    case 'in_stock':
      return 1;
    case 'in_stock_preorder':
      return 2;
    case 'incoming':
      return 3;
    case 'not_in_stock':
      return 4;
    case 'unknown':
      return 5;
    default:
      return 6;
  }
};

const sortShipping = (a: SortablePrice, b: SortablePrice): number => {
  const s1 = a.shipping?.fastest?.deliveryDays ?? null;
  const s2 = b.shipping?.fastest?.deliveryDays ?? null;
  if (s1 === null && s2 !== null) return placeAfter;
  if (s1 !== null && s2 === null) return placeBefore;
  if (s1 === null && s2 === null) return 0;
  if (s1!.min !== s2!.min) {
    return s1!.min - s2!.min;
  }
  return s1!.max - s2!.max;
};

const sortAvailability = (a: SortablePrice, b: SortablePrice): number => {
  const aDate = a.availability?.availabilityDate
    ? new Date(a.availability?.availabilityDate)
    : null;
  const bDate = b.availability?.availabilityDate
    ? new Date(b.availability?.availabilityDate)
    : null;

  if (aDate === null && bDate !== null) return placeAfter;
  if (aDate !== null && bDate === null) return placeBefore;
  if (aDate === null || bDate === null) return 0;
  return aDate.getTime() - bDate.getTime();
};

const sortByFastest = (
  offerList: SortablePrice[],
  filters: IFilter[],
  isAlternative: Boolean = false
): SortablePrice[] => {
  if (offerList.length <= 0) {
    return offerList;
  }

  const sortedOffersAndAlternatives = [...offerList];

  sortedOffersAndAlternatives.sort((a, b) => {
    const downloadableSort = sortDownloadable(a, b);
    if (downloadableSort) {
      return downloadableSort;
    }

    const featuredSort = sortFeatured(a, b);
    if (featuredSort && !isAlternative) {
      return featuredSort;
    }

    const stockStatusSort = sortStockStatus(a, b);
    if (stockStatusSort) {
      return stockStatusSort;
    }

    const shippingSort = sortShipping(a, b);
    if (shippingSort) {
      return shippingSort;
    }

    const availabilitySort = sortAvailability(a, b);
    if (availabilitySort) {
      return availabilitySort;
    }

    return 0;
  });

  return sortedOffersAndAlternatives;
};

const sortAlternativePricesByFastestDelivery = (
  offer: ExtendedPrice,
  filters: IFilter[]
) => {
  const altOffers = offer.alternativePrices ? [...offer.alternativePrices] : [];

  if (!altOffers.length) return offer.alternativePrices;

  return sortByFastest(
    altOffers as AlternativePrice[],
    filters,
    true
  ) as AlternativePrice[];
};

export const sortByFastestDelivery = (
  offerList: IListPrices,
  currentSorting: OptionTypeEnum,
  filters: IFilter[] = []
) => {
  const newOffers = { ...offerList };
  if (currentSorting === OptionTypeEnum.FASTEST_DELIVERY) {
    newOffers.mainList = sortByFastest(
      [...newOffers.mainList],
      filters
    ) as ExtendedPrice[];

    for (const offer of newOffers.mainList) {
      offer.alternativePrices = sortAlternativePricesByFastestDelivery(
        offer,
        filters
      );
    }

    newOffers.mainList = newOffers.mainList.map(offer =>
      replaceMainOfferWithAlternativeOffer(
        offer,
        offer.alternativePrices?.[0] as AlternativePrice
      )
    );
  }
  return newOffers;
};
